// domain/model/PolicyholderDemographics.js

/**
 * What a payer needs to know about the human behind a policy.
 *
 * Revenue Cycle's own read-only view over a person record, and the context's FIRST projection of a
 * model it does not own. Everything else here is either this context's subject matter or a shared
 * identifier it references without interpreting; this is neither. It is another area's record, read
 * through a mapper that renames and re-types it into terms a payer would recognise.
 *
 * READ-ONLY, AND NOT PUBLISHED. Nothing outside this context sees it. Revenue Cycle never writes a
 * person record through this shape either: creating one goes through a callable that owns the field
 * allowlist, because a projection built for reading is the wrong thing to write back through.
 *
 * DESCRIBES WHOEVER HOLDS THE POLICY, including the client when they hold their own. Readiness
 * applies to a self policyholder as much as to a parent, so this is not a "someone else" shape.
 *
 * NO SNAPSHOT IS TAKEN. This is derived on read and never stored on the coverage. See
 * Coverage.types for why.
 *
 * `personId` IS A REFERENCE, NOT AN IDENTITY, which is why this carries no `@identity` and is a value
 * object rather than an entity. It says which person these facts describe; it does not identify the
 * description. Two of these holding the same facts are interchangeable, and nothing anywhere needs to
 * tell one instance from another. The person has identity; this does not.
 *
 * @domainObject value-object
 * @invariant Every field is a fact a payer asks for, or absent. Nothing here is a status, a role or
 *   a flag: a person is a policyholder because a coverage points at them, never because of anything
 *   recorded on the person.
 * @invariant `gender` is null when the record says unknown. The stored vocabulary carries an explicit
 *   "unknown" member, which is a record of not knowing rather than an answer, and readiness has to
 *   treat it as missing or it would pass a coverage a payer will reject.
 * @invariant `dateOfBirth` is a calendar date, never an instant. The person record stores an
 *   instant; the mapper is where that stops being true.
 * @typedef {Object} PolicyholderDemographics
 * @property {string} personId
 * @property {string|null} firstName
 * @property {string|null} lastName
 * @property {string|null} fullName - as the product displays it, which is not always the two parts
 * @property {string|null} dateOfBirth - 'YYYY-MM-DD'
 * @property {'male'|'female'|'other'|null} gender
 * @property {string|null} addressLine1
 * @property {string|null} city
 * @property {string|null} state
 * @property {string|null} postalCode
 */

/**
 * What we know about a policyholder we looked for and did not find: nothing, said explicitly.
 *
 * ABSENT AND UNREADABLE ARE DIFFERENT FACTS, and collapsing them hides the worse one. A consumer
 * that gets no record at all cannot tell "the read has not come back" from "there is no such
 * person", and readiness stays quiet for the first, correctly, because reporting every requirement
 * mid-read is a problem that appears to fix itself. Handing back this instead says the read
 * finished, so readiness reports what a payer wants and the row flags a coverage that cannot bill.
 *
 * Every field null rather than a status flag: a person we cannot read is one we know nothing about,
 * which is exactly what this shape already has a way to say.
 *
 * @param {string} personId - the reference that did not resolve
 * @returns {PolicyholderDemographics}
 */
// eslint-disable-next-line import/prefer-default-export
export function unreadablePolicyholder(personId) {
  return {
    personId,
    firstName: null,
    lastName: null,
    fullName: null,
    dateOfBirth: null,
    gender: null,
    addressLine1: null,
    city: null,
    state: null,
    postalCode: null,
  };
} // unreadablePolicyholder()
